<template>
  <div class="border-2 px-3 py-4 radius-20 border-spect-gray overflow-x-auto min-w-[60rem]">
    <DataTable
      :value="users"
      :stripedRows="true"
      :paginator="users.length > 10"
      :rows="10"
      :rowsPerPageOptions="[10, 20, 50, 100]"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="$t('common.pagination_template')"
      class="p-datatable-sm min-w-[60rem]"
      :emptyMessage="$t('statistics.no_users')"
    >
      <!-- Nom complet -->
      <Column
        :header="$t('statistics.full_name')"
        sortable
        sortField="fullName"
        class="white-space-nowrap"
      >
        <template #body="{ data }">
          <div class="flex items-center gap-2">
            <Avatar
              :label="getInitials(data)"
              shape="circle"
              size="normal"
              class="me-2"
            />
            <span class="no-whitespace">
              {{ data.firstname }} {{ data.lastname }}
            </span>
          </div>
        </template>
      </Column>

      <!-- Email -->
      <Column field="email" :header="$t('statistics.email')" sortable>
        <template #body="{ data }">
          <a :href="`mailto:${data.email}`" class="email-link">
            {{ data.email }}
          </a>
        </template>
      </Column>

      <!-- CyberScore -->
      <Column
        field="cyberscore"
        :header="$t('statistics.cyberscore')"
        sortable
      >
        <template #body="{ data }">
          <div class="flex items-center gap-2 w-32">
            <ProgressBar
              :value="Number(data.cyberscore) || 0"
              :showValue="false"
              class="flex-1 h-2"
            />
            <span class="text-xs font-medium">
              {{ Number(data.cyberscore) || 0 }}%
            </span>
          </div>
        </template>
      </Column>

      <!-- Progression -->
      <Column
        field="progression"
        :header="$t('statistics.progression')"
        sortable
      >
        <template #body="{ data }">
          <div class="flex items-center gap-2 w-32">
            <ProgressBar
              :value="Number(data.progression) || 0"
              :showValue="false"
              class="flex-1 h-2"
            />
            <span class="text-xs font-medium">
              {{ Number(data.progression) || 0 }}%
            </span>
          </div>
        </template>
      </Column>

      <!-- Score certification -->
      <Column
        field="cert_score_avg"
        :header="$t('statistics.cert_score')"
        sortable
      >
        <template #body="{ data }">
          {{ Number(data.cert_score_avg) || 0 }}
        </template>
      </Column>

      <!-- Inscription -->
      <Column
        field="registration_date"
        :header="$t('statistics.registration')"
        sortable
        class="white-space-nowrap"
      >
        <template #body="{ data }">
          {{ formatDate(data.registration_date) }}
        </template>
      </Column>

      <!-- Démarrage -->
      <Column
        field="start_date"
        :header="$t('statistics.start_date')"
        sortable
        class="white-space-nowrap"
      >
        <template #body="{ data }">
          <span v-if="data.start_date">{{ formatDate(data.start_date) }}</span>
          <span v-else class="text-gray-400 italic">
            {{ $t("statistics.not_started") }}
          </span>
        </template>
      </Column>

      <!-- Finalisation -->
      <Column
        field="finalization_date"
        :header="$t('statistics.finalization_date')"
        sortable
        class="white-space-nowrap"
      >
        <template #body="{ data }">
          <span v-if="data.finalization_date">
            {{ formatDate(data.finalization_date) }}
          </span>
          <span v-else class="text-gray-400">-</span>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup>
import { defineProps } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Avatar from "primevue/avatar";
import ProgressBar from "primevue/progressbar";

defineProps({
  users: {
    type: Array,
    default: () => [],
  },
});

const getInitials = (user) => {
  const first = user?.firstname?.charAt(0) || "";
  const last = user?.lastname?.charAt(0) || "";
  return (first + last).toUpperCase() || "?";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};
</script>

<style lang="scss" scoped>
@use "../../assets/scss/variables.scss" as *;
@use "../../assets/scss/app/common.scss" as *;

.email-link {
  color: #06bbc4;
  text-decoration: none;
}
.email-link:hover {
  text-decoration: underline;
}
.me-2 {
  margin-right: 0.5rem;
  background: var(--p-avatar-background) !important;
}
</style>
